import React, { RefObject, useState } from 'react';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { PlanningPageType } from './PlanningPage';
import { useCurrentPeriod } from '../hooks/incidentHooks';
import { getIncidentCardTitle, getIncidentCardRailLabel } from './USCGPage';
import { useICSContext } from './ICSContext';
import {
  useIncidentCalendarEvents,
  IncidentCalendarDisplayEvent,
} from '../hooks/incidentCalendarHooks';
import CalendarEventEditDrawer from '../CalendarEventEditDrawer';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { useUpdateOperationalPeriod } from 'hooks/incident/useIncidentManagement';
import { selectIncidentGuid } from 'slices/commonSelectors';
import { useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Check, ListChecks, ChevronLeft } from 'lucide-react';
import { PlanningFormStatusDot } from './PlanningFormStatusDot';
import { formatStepperFormStatusLabel } from './stepperFormStatus';
import { usePlanningStepperFormStatuses } from './usePlanningStepperFormStatuses';

const REMOVED_WIDGETS = ['agenda', 'initialResponse'];

type PlanningStepperProps = {
  planningPhases: PlanningPageType[];
  currentPhase: number;
  updatePhase: (phase: number) => void;
  isOpen?: boolean;
  onHide?: () => void;
  isAgendaOpen?: boolean;
  onToggleAgenda?: () => void;
  mainContentRef?: RefObject<HTMLDivElement>;
};

function phaseForms(
  phase: PlanningPageType
): { widgetName: string; title: string; railLabel: string }[] {
  const order =
    phase.widgetOrder?.filter((w) => !REMOVED_WIDGETS.includes(w)) ?? [];
  return order.map((widgetName) => ({
    widgetName,
    title: getIncidentCardTitle(widgetName),
    railLabel: getIncidentCardRailLabel(widgetName),
  }));
}

const PlanningStepper: React.FC<PlanningStepperProps> = ({
  planningPhases,
  currentPhase,
  updatePhase,
  isOpen = true,
  onHide,
  isAgendaOpen = false,
  onToggleAgenda,
  mainContentRef,
}) => {
  const currentPeriod = useCurrentPeriod();
  const { card, setCard } = useICSContext();
  const formStatuses = usePlanningStepperFormStatuses();
  const { data: calendarEvents } = useIncidentCalendarEvents();
  const mutation = useUpdateOperationalPeriod();
  const incidentId = useSelector(selectIncidentGuid);

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [meetingEvent, setMeetingEvent] =
    useState<IncidentCalendarDisplayEvent | null>(null);

  const completedThrough =
    currentPeriod.currentPeriod?.phase_number_completed ?? -1;
  const isLastPhase = currentPhase === planningPhases.length - 1;
  const isPhaseCompleted = currentPhase <= completedThrough;
  const isNextToComplete = currentPhase === completedThrough + 1;

  const handleFormClick = (phaseIndex: number, widgetName: string) => {
    if (phaseIndex !== currentPhase) {
      updatePhase(phaseIndex);
    }
    setCard(widgetName);
  };

  const completePhaseLabel = isPhaseCompleted
    ? 'Completed'
    : isLastPhase
      ? 'Final phase'
      : 'Complete Phase';

  const completePhaseDisabled =
    isPhaseCompleted || isLastPhase || !isNextToComplete;

  const completePhaseTitle = isPhaseCompleted
    ? 'This phase is already complete'
    : !isNextToComplete && !isLastPhase
      ? 'Complete earlier phases first'
      : undefined;

  const phaseMarkerClass = (isActive: boolean, isCompleted: boolean) =>
    cn(
      'tw:mt-0.5 tw:inline-flex tw:h-5 tw:w-5 tw:shrink-0 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-full tw:border tw:text-[10px] tw:font-semibold',
      isCompleted
        ? 'tw:border-emerald-500 tw:bg-emerald-950 tw:text-emerald-400'
        : isActive
          ? 'tw:border-primary tw:bg-primary tw:text-primary-foreground'
          : 'tw:border-white tw:bg-background tw:text-muted-foreground tw:hover:bg-muted/40'
    );

  const handleCompletePhase = () => {
    if (!isLastPhase) {
      updatePhase(currentPhase + 1);
    }
    mutation.mutate({
      incidentId: incidentId,
      newEndTime:
        currentPeriod.currentPeriod?.anticipated_end_date_time || undefined,
      operationalPeriod: currentPeriod.currentPeriod?.period_number || 0,
      phaseNumberCompleted: currentPhase,
      showToast: false,
    });
    if (mainContentRef?.current) {
      mainContentRef.current.scrollTop = 0;
    }
    setShowCompleteModal(false);
  };

  const openMeetingDetailsForPhase = (phaseTitle: string) => {
    const event = calendarEvents?.find(
      (ev) => ev.eventTypeLabel === phaseTitle
    );
    if (event) {
      setMeetingEvent(event);
      setShowEditDrawer(true);
    }
  };

  const phaseHasMeeting = (phaseTitle: string) =>
    calendarEvents?.some((ev) => ev.eventTypeLabel === phaseTitle) ?? false;

  if (!isOpen) {
    return (
      <TooltipProvider>
        <nav
          className="dark planning-stepper planning-stepper--collapsed tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:items-center tw:border-0 tw:bg-background tw:px-2 tw:py-2"
          aria-label="Planning phases"
          data-tour="planning-stepper"
        >
          {planningPhases.map((phase, index) => {
            const isActive = index === currentPhase;
            const isCompleted = index <= completedThrough;
            return (
              <React.Fragment key={phase.title}>
                <div className="tw:flex tw:w-full tw:shrink-0 tw:justify-center">
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <button
                          type="button"
                          className={phaseMarkerClass(isActive, isCompleted)}
                          onClick={() => updatePhase(index)}
                          aria-label={phase.title}
                          aria-current={isActive ? 'step' : undefined}
                        >
                          {isCompleted ? (
                            <Check className="tw:h-3 tw:w-3" aria-hidden />
                          ) : (
                            index + 1
                          )}
                        </button>
                      }
                    />
                    <TooltipContent side="right">{phase.title}</TooltipContent>
                  </Tooltip>
                </div>
                {index < planningPhases.length - 1 && (
                  <div
                    className={cn(
                      'tw:w-px tw:min-h-0 tw:flex-1',
                      isCompleted ? 'tw:bg-primary/60' : 'tw:bg-border'
                    )}
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <nav
        className="dark planning-stepper planning-stepper--expanded tw:relative tw:z-10 tw:flex tw:h-full tw:w-64 tw:shrink-0 tw:flex-col tw:overflow-hidden tw:border-r tw:border-border tw:bg-background tw:shadow-lg"
        aria-label="Planning-P workflow"
        data-tour="planning-stepper"
      >
        <div className="tw:flex tw:shrink-0 tw:items-start tw:justify-between tw:gap-2 tw:border-b tw:border-border tw:px-3 tw:py-3">
          <div className="tw:min-w-0">
            <p className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
              Planning-P
            </p>
          </div>
          {onHide && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      type="button"
                      variant="outline"
                      className="tw:h-9 tw:w-9 tw:shrink-0 tw:p-0"
                      onClick={onHide}
                      aria-label="Collapse planning stepper"
                    >
                      <ChevronLeft className="tw:h-4 tw:w-4" aria-hidden />
                    </Button>
                  }
                />
                <TooltipContent side="bottom">
                  Collapse planning stepper
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        <ol className="tw:mb-0 tw:min-h-0 tw:flex-1 tw:list-none tw:space-y-0 tw:overflow-y-auto tw:!px-2 tw:py-3">
          {planningPhases.map((phase, index) => {
            const isActive = index === currentPhase;
            const isCompleted = index <= completedThrough;
            const forms = phaseForms(phase);
            const showMeeting = isActive && phaseHasMeeting(phase.title);

            return (
              <li
                key={phase.title}
                className="tw:relative tw:pb-3 tw:last:pb-0"
              >
                {index < planningPhases.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'tw:absolute tw:top-6 tw:left-[0.69rem] tw:h-[calc(100%-0.35rem)] tw:w-px',
                      isCompleted ? 'tw:bg-primary/60' : 'tw:bg-border'
                    )}
                  />
                )}
                <div
                  className={cn(
                    'tw:relative tw:rounded-md tw:px-1.5 tw:py-1.5 tw:transition-colors',
                    isActive
                      ? 'tw:bg-primary/10 tw:ring-1 tw:ring-primary/30'
                      : 'tw:hover:bg-muted/40'
                  )}
                >
                  <div className="tw:flex tw:items-start tw:gap-2">
                    <button
                      type="button"
                      onClick={() => updatePhase(index)}
                      className={phaseMarkerClass(isActive, isCompleted)}
                      aria-label={`Select ${phase.title}`}
                    >
                      {isCompleted ? (
                        <Check className="tw:h-3 tw:w-3" aria-hidden />
                      ) : (
                        index + 1
                      )}
                    </button>

                    <div className="tw:min-w-0 tw:flex-1">
                      <div className="tw:min-w-0 tw:flex-1">
                        <button
                          type="button"
                          onClick={() => updatePhase(index)}
                          className="tw:w-full tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-left"
                          aria-current={isActive ? 'step' : undefined}
                        >
                          <span
                            className={cn(
                              'tw:block tw:text-xs tw:font-medium tw:leading-snug',
                              isActive
                                ? 'tw:text-foreground'
                                : 'tw:text-foreground/90'
                            )}
                          >
                            {phase.title}
                          </span>
                        </button>
                        {showMeeting ? (
                          <button
                            type="button"
                            className="tw:mt-0.5 tw:block tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-left tw:text-[10px] tw:leading-snug tw:text-muted-foreground tw:underline-offset-2 tw:hover:text-foreground tw:hover:underline"
                            data-tour="planning-meeting-details"
                            onClick={() =>
                              openMeetingDetailsForPhase(phase.title)
                            }
                            aria-label={`Meeting Details for ${phase.title}`}
                          >
                            Meeting Details
                          </button>
                        ) : null}
                      </div>

                      {isActive &&
                        phase.agenda.length > 0 &&
                        onToggleAgenda && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="tw:mt-1.5 tw:h-6 tw:w-full tw:justify-start tw:gap-1.5 tw:px-2 tw:text-[10px]"
                            data-tour="planning-show-agenda"
                            aria-pressed={isAgendaOpen}
                            aria-label={
                              isAgendaOpen ? 'Hide Agenda' : 'Show Agenda'
                            }
                            onClick={() => onToggleAgenda()}
                          >
                            <ListChecks
                              className="tw:h-3.5 tw:w-3.5 tw:shrink-0"
                              aria-hidden
                            />
                            {isAgendaOpen ? 'Hide Agenda' : 'Show Agenda'}
                          </Button>
                        )}

                      {forms.length > 0 ? (
                        <ul className="tw:mt-1.5 tw:space-y-0.5">
                          {forms.map(({ widgetName, title, railLabel }) => {
                            const formActive = isActive && card === widgetName;
                            const formStatus = formStatuses[widgetName];
                            const statusLabel = formatStepperFormStatusLabel(
                              railLabel,
                              formStatus
                            );
                            return (
                              <li key={widgetName}>
                                <button
                                  type="button"
                                  title={
                                    formStatus && formStatus.status !== 'empty'
                                      ? `${title} — ${statusLabel}`
                                      : title
                                  }
                                  aria-label={statusLabel}
                                  className="tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:gap-1.5 tw:rounded tw:border-0 tw:bg-transparent tw:px-0.5 tw:py-0.5 tw:text-left tw:text-[10px] tw:hover:bg-muted/60"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleFormClick(index, widgetName);
                                  }}
                                >
                                  <PlanningFormStatusDot
                                    info={formStatus}
                                    widgetName={widgetName}
                                    formActive={formActive}
                                  />
                                  <span
                                    className={cn(
                                      'tw:font-medium tw:underline-offset-2 tw:hover:underline',
                                      formActive
                                        ? 'tw:text-foreground'
                                        : 'tw:text-foreground/90'
                                    )}
                                  >
                                    {railLabel}
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}

                      {isActive ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="tw:mt-2 tw:h-7 tw:w-full tw:text-[10px]"
                          data-tour="planning-complete-phase"
                          disabled={completePhaseDisabled}
                          title={completePhaseTitle}
                          aria-label={completePhaseTitle ?? completePhaseLabel}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (completePhaseDisabled) return;
                            setShowCompleteModal(true);
                          }}
                        >
                          {completePhaseLabel}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </nav>

      <CustomModal
        show={showCompleteModal}
        heading={'Complete Phase ' + (currentPhase + 1) + '?'}
        onClose={() => setShowCompleteModal(false)}
        onConfirm={handleCompletePhase}
        saveButton={'Complete Phase ' + (currentPhase + 1)}
        buttonDisable={false}
        icon={<SharedIcon iconName="warning" color="primary" size="lg" />}
        staticBackdrop={false}
      >
        {!isLastPhase ? (
          <div className="p-2">
            You are about to complete Phase {currentPhase + 1} and advance to
            Phase {currentPhase + 2}.
          </div>
        ) : (
          <div className="p-2">
            You are about to complete Phase {currentPhase + 1}.
          </div>
        )}
      </CustomModal>
      {showEditDrawer && meetingEvent && (
        <CalendarEventEditDrawer
          toggle={() => {
            setShowEditDrawer(false);
            setMeetingEvent(null);
          }}
          selectedEntity={meetingEvent}
        />
      )}
    </TooltipProvider>
  );
};

export default PlanningStepper;
